// https://github.com/tommymcglynn/mortgage-js
import { PROVIDERS_OUTSIDE_US } from "../../../../../lib/constant";

const defaultPrice = 500000;
const defaultDownPayment = 100000;
const defaultInterestRate = 0.05;
const defaultMonths = 360;
const defaultTaxRate = 0.0126;
const defaultInsuranceRate = 0.0014;
const defaultMortgageInsuranceRate = 0.011;
const defaultMortgageInsuranceEnabled = true;
const defaultMortgageInsuranceThreshold = 0.2;
const defaultAdditionalPrincipalPayment = 0;
const defaultDatastoreProviderId = 0;

class MortgageCalculator {
  constructor() {
    this.totalPrice = defaultPrice;
    this.downPayment = defaultDownPayment;
    this.interestRate = defaultInterestRate;
    this.months = defaultMonths;
    this.taxRate = defaultTaxRate;
    this.insuranceRate = defaultInsuranceRate;
    this.mortgageInsuranceRate = defaultMortgageInsuranceRate;
    this.mortgageInsuranceEnabled = defaultMortgageInsuranceEnabled;
    this.mortgageInsuranceThreshold = defaultMortgageInsuranceThreshold;
    this.additionalPrincipalPayment = defaultAdditionalPrincipalPayment;
    this.datastoreProviderId = defaultDatastoreProviderId;
  }

  calculatePayment() {
    let loanAmount = this.totalPrice - this.downPayment;
    let paymentSchedule = MortgageCalculator.calculatePaymentSchedule(
      loanAmount,
      this.interestRate,
      this.months,
      this.additionalPrincipalPayment,
      this.datastoreProviderId
    );
    let piPayment = paymentSchedule.length
      ? paymentSchedule[0].totalPayment
      : 0;
    let downPaymentPercentage = this.downPayment / this.totalPrice;
    let mortgageInsurance = 0;
    if (
      this.mortgageInsuranceEnabled &&
      downPaymentPercentage < this.mortgageInsuranceThreshold
    ) {
      mortgageInsurance = (loanAmount * this.mortgageInsuranceRate) / 12;
    }

    let propertyTax = (this.totalPrice * this.taxRate) / 12;
    let homeOwnerInsurance = MortgageCalculator.roundPenny(
      (this.totalPrice * this.insuranceRate) / 12
    );
    return {
      loanAmount,
      principalAndInterest: piPayment,
      tax: propertyTax,
      insurance: homeOwnerInsurance,
      total: piPayment + propertyTax + homeOwnerInsurance + mortgageInsurance,
      termMonths: this.months,
      paymentSchedule,
      mortgageInsurance,
    };
  }

  static calculatePaymentSchedule(
    loanAmount,
    annualRate,
    termMonths,
    additionalPrincipalPayments = 0,
    datastoreProviderId
  ) {
    let monthlyRate = annualRate / 12;
    if (PROVIDERS_OUTSIDE_US.includes(datastoreProviderId)) {
      let compoundingRate = 2;
      monthlyRate = Math.pow(1 + annualRate / compoundingRate, 1 / 6) - 1;
    }

    const monthlyPayment = MortgageCalculator.calculateMonthlyPIPayment(
      loanAmount,
      monthlyRate,
      termMonths
    );
    let principal = MortgageCalculator.roundPenny(loanAmount);
    let payments = [];
    let totalInterest = 0;
    let totalPayments = 0;
    let i = 0;
    while (principal > 0 && i < termMonths) {
      let interestPayment = MortgageCalculator.roundPenny(
        principal * monthlyRate
      );
      let principalPayment = MortgageCalculator.roundPenny(
        monthlyPayment - interestPayment + additionalPrincipalPayments
      );
      if (principal > principalPayment) {
        principal = MortgageCalculator.roundPenny(principal - principalPayment);
      } else {
        principalPayment = principal;
        principal = 0;
      }
      let totalPayment = interestPayment + principalPayment;
      totalInterest += interestPayment;
      totalPayments += totalPayment;
      payments[i] = {
        count: i + 1,
        interestPayment,
        totalInterest,
        principalPayment,
        totalPayment,
        totalPayments,
        balance: principal,
      };
      i++;
    }

    return payments;
  }

  static calculateMonthlyPIPayment(loanAmount, monthlyRate, termMonths) {
    let payment =
      (monthlyRate * loanAmount * Math.pow(1 + monthlyRate, termMonths)) /
      (Math.pow(1 + monthlyRate, termMonths) - 1);
    return this.nextPenny(payment);
  }

  static roundPenny(value) {
    return Math.round(value * 100) / 100;
  }

  static nextPenny(value) {
    return Math.ceil(value * 100) / 100;
  }
}

const _calc = new MortgageCalculator();

module.exports = {
  createMortgageCalculator() {
    return new MortgageCalculator();
  },
  calculatePayment(
    totalPrice = defaultPrice,
    downPayment = defaultDownPayment,
    interestRate = defaultInterestRate,
    months = defaultMonths,
    taxRate = defaultTaxRate,
    insuranceRate = defaultInsuranceRate,
    mortgageInsuranceRate = defaultMortgageInsuranceRate,
    mortgageInsuranceEnabled = defaultMortgageInsuranceEnabled,
    mortgageInsuranceThreshold = defaultMortgageInsuranceThreshold,
    additionalPrincipalPayment = defaultAdditionalPrincipalPayment,
    datastoreProviderId = defaultDatastoreProviderId
  ) {
    _calc.totalPrice = totalPrice;
    _calc.downPayment = downPayment;
    _calc.interestRate = interestRate;
    _calc.months = months;
    _calc.taxRate = taxRate;
    _calc.insuranceRate = insuranceRate;
    _calc.mortgageInsuranceRate = mortgageInsuranceRate;
    _calc.mortgageInsuranceEnabled = mortgageInsuranceEnabled;
    _calc.mortgageInsuranceThreshold = mortgageInsuranceThreshold;
    _calc.additionalPrincipalPayment = additionalPrincipalPayment;
    _calc.datastoreProviderId = datastoreProviderId;
    return _calc.calculatePayment();
  },
  nextPenny: MortgageCalculator.nextPenny,
};
